import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { Banner, Button, ConfirmDialog, Spinner } from "@/components/ui";
import {
  approveDeviceRequest,
  endSession,
  errorDetail,
  listActiveSessions,
  listBlockedDevices,
  listDeviceRequests,
  rejectDeviceRequest,
  unblockDevice,
} from "@/lib/api";
import type { ActiveSession, BlockedDevice, DeviceRequest } from "@/lib/types";
import { formatDateTime } from "@/lib/format";
import { getStoredUser } from "@/lib/auth";
import { RefreshCw } from "lucide-react";
import { useLanguage } from "@/i18n";

/**
 * Every account at once: who is signed in now (with a per-session End), phones
 * waiting for approval, and blocked phones. The per-account view of the same
 * things is AccountSecurityDialog on the Accounts page.
 */
export default function SessionsPage() {
  const { t, lang } = useLanguage();
  const p = t.sessionsPage;
  const s = t.accountSecurity;
  const [requests, setRequests] = useState<DeviceRequest[] | null>(null);
  const [active, setActive] = useState<ActiveSession[] | null>(null);
  const [blocked, setBlocked] = useState<BlockedDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState<ActiveSession | null>(null);
  const [allowFinalSync, setAllowFinalSync] = useState(true);
  const me = getStoredUser();

  const when = (iso: string | null | undefined) =>
    iso ? formatDateTime(iso, lang, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, a, b] = await Promise.all([
        listDeviceRequests(),
        listActiveSessions(),
        listBlockedDevices(),
      ]);
      setRequests(r);
      setActive(a);
      setBlocked(b);
    } catch (err) {
      setError(errorDetail(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>, notice: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      toast.success(notice);
      setEnding(null);
      await load();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(false);
    }
  }

  const accountName = (x: { username: string | null; full_name: string | null }) =>
    x.full_name ? `${x.full_name} (${x.username})` : x.username ?? "—";

  const th = "px-2.5 py-1.5 font-semibold";
  const td = "px-2.5 py-1.5 align-top";

  return (
    <AppShell crumbs={[{ label: p.crumb }]}>
      <div className="mx-auto max-w-[1300px] space-y-5 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-ink">{p.heading}</h1>
            <p className="mt-0.5 text-xs text-ink-muted">{p.subheading}</p>
          </div>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void load()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            {p.refresh}
          </Button>
        </div>

        {error && <Banner tone="danger">{error}</Banner>}

        {/* Waiting for approval */}
        <Card title={p.pendingHeading} count={requests?.length}>
          {requests === null ? (
            <Loading />
          ) : requests.length === 0 ? (
            <Empty text={p.pendingNone} />
          ) : (
            <Table minWidth={720}>
              <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className={th}>{p.colAccount}</th>
                  <th className={th}>{p.colDevice}</th>
                  <th className={th}>{p.colLastActivity}</th>
                  <th className={th}>{p.colIp}</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {requests.map((r) => (
                  <tr key={r.id}>
                    <td className={`${td} text-ink`}>{accountName(r)}</td>
                    <td className={`${td} text-ink-secondary`}>
                      {r.device_model ?? r.device_id.slice(0, 8)}
                      {r.app_version && <span className="ml-1 text-2xs text-ink-muted">v{r.app_version}</span>}
                      {r.replaces_model && (
                        <p className="text-2xs text-amber-800">{s.pendingReplaces(r.replaces_model)}</p>
                      )}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-ink-secondary`}>{when(r.last_attempt_at)}</td>
                    <td className={`${td} font-mono text-2xs text-ink-muted`}>{r.ip ?? "—"}</td>
                    <td className={`${td} whitespace-nowrap text-right`}>
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={busy}
                          onClick={() => void act(() => approveDeviceRequest(r.id), s.noticeApproved(r.username ?? ""))}
                        >
                          {s.approve}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => void act(() => rejectDeviceRequest(r.id, false), s.noticeRejected(r.username ?? ""))}
                        >
                          {s.reject}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busy}
                          onClick={() => void act(() => rejectDeviceRequest(r.id, true), s.noticeRejected(r.username ?? ""))}
                        >
                          {s.rejectBlock}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Signed in now */}
        <Card title={p.activeHeading} count={active?.length}>
          {active === null ? (
            <Loading />
          ) : active.length === 0 ? (
            <Empty text={p.activeNone} />
          ) : (
            <Table minWidth={760}>
              <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className={th}>{p.colAccount}</th>
                  <th className={th}>{p.colDevice}</th>
                  <th className={th}>{p.colStarted}</th>
                  <th className={th}>{p.colLastActivity}</th>
                  <th className={th}>{p.colIp}</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {active.map((x) => (
                  <tr key={x.id}>
                    <td className={`${td} text-ink`}>
                      {accountName(x)}
                      <span className="ml-1 text-2xs text-ink-muted">{x.role}</span>
                    </td>
                    <td className={`${td} text-ink-secondary`}>
                      {x.client === "portal" ? s.sessionBrowser : x.device_model ?? x.device_id?.slice(0, 8) ?? "—"}
                      {x.app_version && <span className="ml-1 text-2xs text-ink-muted">v{x.app_version}</span>}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-ink-secondary`}>{when(x.started_at)}</td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-ink-secondary`}>
                      {when(x.last_seen_at)}
                      <p className="text-2xs text-ink-muted">{p.idleFor(x.idle_minutes)}</p>
                    </td>
                    <td className={`${td} font-mono text-2xs text-ink-muted`}>{x.ip ?? "—"}</td>
                    <td className={`${td} text-right`}>
                      {x.user_id !== me?.id && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busy}
                          onClick={() => {
                            setAllowFinalSync(true);
                            setEnding(x);
                          }}
                        >
                          {s.endSession}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Blocked phones */}
        <Card title={p.blockedHeading} count={blocked?.length}>
          {blocked === null ? (
            <Loading />
          ) : blocked.length === 0 ? (
            <Empty text={p.blockedNone} />
          ) : (
            <Table minWidth={640}>
              <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className={th}>{p.colDevice}</th>
                  <th className={th}>{p.colAccount}</th>
                  <th className={th}>{p.colBlocked}</th>
                  <th className={th}>{p.colReason}</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {blocked.map((b) => (
                  <tr key={b.id}>
                    <td className={`${td} text-ink`} title={b.device_id}>
                      {b.device_model ?? b.device_id.slice(0, 8)}
                    </td>
                    <td className={`${td} text-ink-secondary`}>{b.username ?? "—"}</td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-ink-secondary`}>
                      {p.blockedBy(when(b.blocked_at), b.blocked_by ?? "")}
                    </td>
                    <td className={`${td} text-ink-secondary`}>{b.reason ?? "—"}</td>
                    <td className={`${td} text-right`}>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          void act(() => unblockDevice(b.id), p.noticeUnblocked(b.device_model ?? b.device_id.slice(0, 8)))
                        }
                      >
                        {p.unblock}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={ending !== null}
        title={s.endSessionTitle}
        body={s.endSessionBody(ending ? accountName(ending) : "")}
        confirmLabel={s.endSessionConfirm}
        cancelLabel={t.common.cancel}
        busy={busy}
        onCancel={() => setEnding(null)}
        onConfirm={() => {
          if (ending) void act(() => endSession(ending.id, allowFinalSync), s.noticeSessionEnded);
        }}
      >
        {ending?.client === "app" && (
          <label className="flex cursor-pointer items-start gap-2 rounded bg-surface-sunken p-2.5">
            <input
              type="checkbox"
              className="mt-0.5 accent-accent-solid"
              checked={allowFinalSync}
              onChange={(e) => setAllowFinalSync(e.target.checked)}
              disabled={busy}
            />
            <span>
              <span className="block text-xs font-medium text-ink">{s.allowFinalSync}</span>
              <span className="block text-2xs text-ink-muted">{s.allowFinalSyncHint}</span>
            </span>
          </label>
        )}
      </ConfirmDialog>
    </AppShell>
  );
}

function Card({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-line bg-surface p-3">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
        {title}
        {count !== undefined && count > 0 && (
          <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-ink-secondary">{count}</span>
        )}
      </h2>
      {children}
    </section>
  );
}

function Table({ minWidth, children }: { minWidth: number; children: React.ReactNode }) {
  return (
    <div className="thin-scroll overflow-x-auto rounded border border-line">
      <table className="w-full text-left text-xs" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 py-6 text-xs text-ink-muted">
      <Spinner className="h-4 w-4" />
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="py-2 text-xs text-ink-muted">{text}</p>;
}
